import { useHoverPopoverClose } from '@app/components/base/HoverPopover';
import { CopyLinkAtTimestampButton } from '@app/modules/logger/screens/stream/components/popup/CopyLinkAtTimestampButton';
import { PlayAtTimestampButton } from '@app/modules/logger/screens/stream/components/popup/PlayAtTimestampButton';
import { StreamPopup } from '@app/modules/logger/screens/stream/components/popup/StreamPopup';
import { StreamPopupHeader } from '@app/modules/logger/screens/stream/components/popup/StreamPopupHeader';
import { PersonAppearance, PersonLane } from '@app/modules/persons/appearances/types';
import { toDateTimeStr } from '@mi-front/core';
import FaceOutlinedIcon from '@mui/icons-material/FaceOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import { useCallback } from 'react';
import { PersonLaneIdentity } from '../shared/PersonLaneIdentity';
import { usePersonsTrackCapture } from '../hooks/usePersonsTrackCapture';

type PersonInfoProps = {
  lane: PersonLane;
  /** Interval under the cursor; drives header time and play / copy-link actions. */
  hovered: PersonAppearance;
};

/**
 * Hover popover for a face/voice appearance bar on the Personas track.
 *
 * Shows detection type (face vs voice) and timestamp in the header, the
 * person's avatar/name/occupation in the body, and play / copy-link actions
 * for the hovered interval.
 */
export function PersonInfo({ lane, hovered }: PersonInfoProps) {
  const timeStamp = hovered.from;
  const closePopover = useHoverPopoverClose();
  const { playFromAppearanceClicked, copyLinkClicked } = usePersonsTrackCapture();

  const handlePlayDone = useCallback(() => {
    playFromAppearanceClicked(lane, hovered);
    closePopover?.();
  }, [playFromAppearanceClicked, closePopover, lane, hovered]);

  const handleCopyLinkDone = useCallback(() => {
    copyLinkClicked(lane, hovered);
    closePopover?.();
  }, [copyLinkClicked, closePopover, lane, hovered]);

  return (
    <StreamPopup
      minWidth={300}
      maxWidth={380}
      header={
        <StreamPopupHeader
          icon={hovered.source === 'face' ? <FaceOutlinedIcon /> : <RecordVoiceOverOutlinedIcon />}
          time={toDateTimeStr(timeStamp)}
        />
      }
      footer={
        <>
          <PlayAtTimestampButton time={timeStamp} onDone={handlePlayDone} />
          <CopyLinkAtTimestampButton time={timeStamp} onDone={handleCopyLinkDone} />
        </>
      }
    >
      {/* Compact vs cast card (64): pill popover prioritizes time + actions. */}
      <PersonLaneIdentity lane={lane} avatarPx={40} borderWidth={2} />
    </StreamPopup>
  );
}
